import React, { createContext, useState, useContext, useCallback } from 'react';
import Modal from '../components/Modal';

const ModalContext = createContext();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState([]);

  // Helper to generate unique IDs
  const generateId = () => `modal-${Date.now()}-${Math.random()}`;

  // Close a specific modal
  const closeModal = useCallback((id) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id));
  }, []);

  // Show toast notification
  const toast = useCallback((message, variant = 'info', options = {}) => {
    const id = generateId();
    const modal = {
      id,
      type: 'toast',
      variant,
      message,
      title: options.title,
      duration: options.duration || 4000,
      autoClose: options.autoClose !== false,
      onClose: () => closeModal(id)
    };
    setModals((prev) => [...prev, modal]);
    return id;
  }, [closeModal]);

  // Show confirmation dialog
  const confirm = useCallback((message, title = 'Confirm Action', options = {}) => {
    return new Promise((resolve) => {
      const id = generateId();
      const modal = {
        id,
        type: 'confirm',
        title,
        message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        size: options.size || 'small',
        onConfirm: () => {
          closeModal(id);
          resolve(true);
        },
        onCancel: () => {
          closeModal(id);
          resolve(false);
        },
        onClose: () => {
          closeModal(id);
          resolve(false);
        }
      };
      setModals((prev) => [...prev, modal]);
    });
  }, [closeModal]);

  // Show alert dialog
  const alert = useCallback((message, title = 'Alert', options = {}) => {
    return new Promise((resolve) => {
      const id = generateId();
      const modal = {
        id,
        type: 'alert',
        title,
        message,
        size: options.size || 'small',
        onClose: () => {
          closeModal(id);
          resolve();
        }
      };
      setModals((prev) => [...prev, modal]);
    });
  }, [closeModal]);

  // Show custom modal
  const custom = useCallback((options) => {
    const id = generateId();
    const modal = {
      id,
      type: 'custom',
      title: options.title,
      content: options.content,
      message: options.message,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      size: options.size || 'medium',
      showCloseButton: options.showCloseButton !== false,
      onConfirm: options.onConfirm ? () => {
        if (options.onConfirm) options.onConfirm();
        if (options.closeOnConfirm !== false) closeModal(id);
      } : undefined,
      onCancel: options.onCancel ? () => {
        if (options.onCancel) options.onCancel();
        closeModal(id);
      } : undefined,
      onClose: () => {
        if (options.onClose) options.onClose();
        closeModal(id);
      }
    };
    setModals((prev) => [...prev, modal]);
    return id;
  }, [closeModal]);

  // Success toast shorthand
  const success = useCallback((message, title) => {
    return toast(message, 'success', { title });
  }, [toast]);

  // Error toast shorthand
  const error = useCallback((message, title) => {
    return toast(message, 'error', { title });
  }, [toast]);

  // Warning toast shorthand
  const warning = useCallback((message, title) => {
    return toast(message, 'warning', { title });
  }, [toast]);

  // Info toast shorthand
  const info = useCallback((message, title) => {
    return toast(message, 'info', { title });
  }, [toast]);

  const value = {
    toast,
    confirm,
    alert,
    custom,
    success,
    error,
    warning,
    info,
    closeModal
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      {/* Render all active modals */}
      {modals.map((modal) => (
        <Modal key={modal.id} {...modal} />
      ))}
    </ModalContext.Provider>
  );
};

export default ModalContext;
